import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Modal } from '../components/ui/Modal'
import { cn } from '../lib/utils'
import { ReadinessWidget } from '../components/ReadinessWidget'
import { DeliverablesModal } from '../components/DeliverablesModal'

// Real API contract (BriefDetail DTO, camelCase). The page was previously typed
// against the legacy Python snake_case shape and expected `revisions` inline,
// which the Java DTO does not return (there is no GET revisions list endpoint
// yet) — `[...brief.revisions]` threw "revisions is not iterable". Collaborators
// ARE returned inline, so they render from the brief directly (no extra fetch).
interface CollaboratorRow {
  id: string
  userId: string
  permission: string
  addedAt: string | null
}

interface BriefDto {
  id: string
  name: string | null
  status: string
  isLocked: boolean
  currentRevisionNumber: number | null
  data: Record<string, unknown> | null
  readiness: { overall: number | null; grade: string | null } | null
  collaborators: CollaboratorRow[]
  createdAt: string | null
  updatedAt: string | null
}

type Tab = 'form' | 'revisions' | 'collaborators'

const ADMIN_ROLES = new Set(['partner_admin', 'platform_admin', 'superadmin'])

export function BriefDetail() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { user } = useAuth()
  const [tab, setTab] = useState<Tab>('form')
  const [deliverablesOpen, setDeliverablesOpen] = useState(false)
  const [confirmLock, setConfirmLock] = useState<'lock' | 'unlock' | null>(null)

  const { data: brief, isLoading } = useQuery({
    queryKey: ['brief', id],
    queryFn: () => api<BriefDto>(`/api/v1/briefs/${id}`),
    enabled: Boolean(id),
  })

  const lock = useMutation({
    mutationFn: () => api<BriefDto>(`/api/v1/briefs/${id}/lock`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brief', id] }),
  })

  const unlock = useMutation({
    mutationFn: () => api<BriefDto>(`/api/v1/briefs/${id}/unlock`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brief', id] }),
  })

  const canLock = user?.roles.some((r) => ADMIN_ROLES.has(r)) ?? false

  if (isLoading || !brief || !id) {
    return <p className="briefer-list-empty">{t('common.loading')}</p>
  }

  const collaborators = brief.collaborators ?? []

  return (
    <div className="brief-detail">
      <header className="brief-detail__head">
        <div>
          <Link to="/briefs" className="brief-detail__back">
            ← {t('briefs.title')}
          </Link>
          <h1 className="brief-detail__title">{brief.name ?? '—'}</h1>
          <div className="brief-detail__meta">
            <span className={`app-pill${brief.isLocked ? ' app-pill--locked' : ' app-pill--draft'}`}>
              {t(`briefs.status.${brief.isLocked ? 'LOCKED' : 'DRAFT'}`)}
            </span>
            <span className="briefer-table__mono">r{brief.currentRevisionNumber ?? '—'}</span>
          </div>
        </div>
        <div className="brief-detail__actions">
          <Button variant="primary" onClick={() => setDeliverablesOpen(true)}>
            {t('brief_detail.deliverables.open_modal')}
          </Button>
          {canLock && !brief.isLocked && (
            <Button variant="outline" onClick={() => setConfirmLock('lock')}>
              {t('brief_detail.lock.lock')}
            </Button>
          )}
          {canLock && brief.isLocked && (
            <Button variant="outline" onClick={() => setConfirmLock('unlock')}>
              {t('brief_detail.lock.unlock')}
            </Button>
          )}
        </div>
      </header>

      <div className="brief-detail__grid">
        <div>
          <nav className="brief-tabs">
            {(['form', 'revisions', 'collaborators'] as Tab[]).map((tk) => (
              <button
                type="button"
                key={tk}
                onClick={() => setTab(tk)}
                className={cn('brief-tabs__tab', tab === tk && 'brief-tabs__tab--active')}
              >
                {t(`brief_detail.tabs.${tk}`)}
              </button>
            ))}
          </nav>

          {tab === 'form' && (
            <Card>
              <CardContent>
                <pre className="brief-detail__json">{JSON.stringify(brief.data ?? {}, null, 2)}</pre>
              </CardContent>
            </Card>
          )}

          {tab === 'revisions' && (
            <div className="briefer-list">
              <table className="briefer-table">
                <thead>
                  <tr>
                    <th>{t('brief_detail.revisions.number')}</th>
                    <th>{t('brief_detail.revisions.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="briefer-table__mono">r{brief.currentRevisionNumber ?? '—'}</td>
                    <td className="briefer-table__date">
                      {brief.updatedAt ? brief.updatedAt.slice(0, 19).replace('T', ' ') : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="briefer-list-empty" style={{ marginTop: '0.75rem', opacity: 0.7 }}>
                {t(
                  'brief_detail.revisions.history_pending',
                  'El historial completo de revisiones estará disponible próximamente.',
                )}
              </p>
            </div>
          )}

          {tab === 'collaborators' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('brief_detail.collaborators.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {collaborators.length === 0 && (
                  <p className="briefer-list-empty">{t('common.empty')}</p>
                )}
                {collaborators.map((c) => (
                  <div key={c.id} className="collab-row">
                    <div>
                      <p className="collab-row__email">{c.userId}</p>
                      <p className="collab-row__role">{c.permission}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <aside className="brief-detail__aside">
          <ReadinessWidget
            score={brief.readiness?.overall ?? null}
            grade={brief.readiness?.grade ?? null}
          />
          <Card>
            <CardHeader>
              <CardTitle>{t('brief_detail.deliverables.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => setDeliverablesOpen(true)}>
                {t('brief_detail.deliverables.open_modal')}
              </Button>
            </CardContent>
          </Card>
        </aside>
      </div>

      <DeliverablesModal
        open={deliverablesOpen}
        briefId={id}
        onClose={() => setDeliverablesOpen(false)}
      />

      <Modal
        open={confirmLock !== null}
        title={confirmLock === 'lock' ? t('brief_detail.lock.lock') : t('brief_detail.lock.unlock')}
        onClose={() => setConfirmLock(null)}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmLock(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (confirmLock === 'lock') lock.mutate()
                else unlock.mutate()
                setConfirmLock(null)
              }}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <p className="brief-detail__confirm">
          {confirmLock === 'lock'
            ? t('brief_detail.lock.confirm_lock')
            : t('brief_detail.lock.confirm_unlock')}
        </p>
      </Modal>
    </div>
  )
}
